import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { RegularSelectOptionsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import { ITriggerCondition, Trigger } from 'modules/automations/store/models/Trigger'
import { bankAccountFormatOptions } from 'modules/banking/lib/bankAccountNumbersHelpers'

import {
  AllProfessionalCategories,
  ContractTypesOptions,
  CotizationGroupOptions,
  DocumentFolderOptions,
  EmployeeEducationLevelOptions,
  JobCatalogRoles,
  LeaveTypeDefaultOptions,
  LeaveTypeOptions,
  LocationOptions,
  SalaryFrequencyOptions,
  SpainProfessionalCategories,
  TeamOptions,
  WorkingDayTypesOptions,
  booleanOptions,
  countryOptions,
  documentTypeOptions,
  employeeActiveOptions,
  genderOptions,
  reviewStatusOptions,
  workingHoursFrequencyOptions,
} from './options'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  condition?: ITriggerCondition
}

const RegularSelectClass = ({ disabled, form, operation, condition }: Props) => {
  const { data } = useQuery(RegularSelectOptionsDocument)

  const costCenters = data?.finance.costCentersConnection.nodes.map((costCenter) => ({
    value: costCenter.id,
    label: costCenter.name,
  }))

  const employeeGroups = data?.employees.employeeGroupsConnection.nodes.map((employeeGroup) => ({
    value: employeeGroup.id,
    label: employeeGroup.name,
  }))

  const options = (): SelectOption[] => {
    const attr = operation.attribute

    if (condition?.options) return condition.options

    switch (attr) {
      case Trigger.Attributes.review_request_state:
        return reviewStatusOptions()

      case Trigger.Attributes.folder_id:
        return DocumentFolderOptions()

      case Trigger.Attributes.document_type:
        return documentTypeOptions()

      case Trigger.Attributes.leave_type_id:
        return LeaveTypeOptions()

      case Trigger.Attributes.gender:
        return genderOptions()

      case Trigger.Attributes.location_id:
        return LocationOptions()

      case Trigger.Attributes.team_id:
        return TeamOptions()

      case Trigger.Attributes.country:
      case Trigger.Attributes.nationality:
        return countryOptions()

      case Trigger.Attributes.spainProfessionalCategories:
        return SpainProfessionalCategories()

      case Trigger.Attributes.professionalCategory:
        return AllProfessionalCategories()

      case Trigger.Attributes.workingHoursFrequency:
        return workingHoursFrequencyOptions()
      case Trigger.Attributes.bankNumberFormat:
        return bankAccountFormatOptions()
      case Trigger.Attributes.employeeActive:
        return employeeActiveOptions()

      case Trigger.Attributes.leave_type:
        return LeaveTypeDefaultOptions()

      case Trigger.Attributes.educationLevel:
        return EmployeeEducationLevelOptions()

      case Trigger.Attributes.cotizationGroup:
        return CotizationGroupOptions()

      case Trigger.Attributes.salaryFrequency:
        return SalaryFrequencyOptions()

      case Trigger.Attributes.workingDayType:
        return WorkingDayTypesOptions()

      case Trigger.Attributes.contractType:
        return ContractTypesOptions()

      case Trigger.Attributes.jobCatalogRole:
        return JobCatalogRoles()

      case Trigger.Attributes.costCenterId:
        return costCenters || []

      case Trigger.Attributes.employeeGroupId:
        return employeeGroups || []

      default:
        return booleanOptions(attr)
    }
  }

  return (
    <Select
      none={i18n.t('common.none')}
      readonly={disabled}
      field={form.get('value')}
      label={i18n.t('automations.actions.branch.value')}
      options={options()}
    />
  )
}

export const RegularSelect = observer(RegularSelectClass)
